/**
 * Bond Mentor — useLiveData Hook
 *
 * Manages the connection to the proxy server.
 * Automatically detects if the proxy is running (Live Mode)
 * or falls back to demo scenarios (Demo Mode).
 * Handles refresh intervals and data caching.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { checkHealth, fetchAllData, resetAndRecheckLive as runAdminLiveRecheck } from '../services/apiClient';

const DEFAULT_REFRESH_MS = 60000; // 1 minute

export default function useLiveData(refreshIntervalMs = DEFAULT_REFRESH_MS) {
  const [mode, setMode] = useState('CHECKING'); // CHECKING | LIVE | DEMO
  const [liveData, setLiveData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef(null);
  const [forceDemo, setForceDemo] = useState(false);

  /**
   * Check if the proxy server is reachable.
   */
  const detectMode = useCallback(async () => {
    if (forceDemo) {
      setMode('DEMO');
      return;
    }

    try {
      const health = await checkHealth();
      if (health && health.status === 'ok') {
        setMode('LIVE');
        return true;
      }
      setMode('DEMO');
      return false;
    } catch {
      setMode('DEMO');
      return false;
    }
  }, [forceDemo]);

  /**
   * Fetch live data from the proxy.
   */
  const refreshData = useCallback(async () => {
    if (mode !== 'LIVE') return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchAllData();
      if (data && data.mode === 'LIVE' && data.signal) {
        setLiveData(data);
        setLastUpdated(new Date(data.fetchedAt || Date.now()));

        if (data.truthState === 'NO_LIVE_SIGNAL') {
          setError(data.statusMessage || 'Live proxy is running, but API data is incomplete. Showing a safe placeholder signal.');
        } else if (data.truthState === 'PARTIAL') {
          const parts = data.errors?.map((item) => item.source).join(', ');
          setError(data.statusMessage || (parts ? `Partial data — check: ${parts}` : 'Partial data — some APIs may be unavailable'));
        } else {
          setError(null);
        }
      } else if (data && data.mode === 'LIVE') {
        setLiveData(data);
        setLastUpdated(new Date(data.fetchedAt || Date.now()));
        setError(data.statusMessage || 'Live proxy responded, but the signal object is missing.');
      } else {
        setError('Live proxy responded, but no usable data was returned. Check the Node server terminal for the /api/all error.');
      }
    } catch (err) {
      setError(err.message);
      // Don't switch to demo on a single failure — keep trying
    } finally {
      setIsLoading(false);
    }
  }, [mode]);

  /**
   * Toggle between Live and Demo mode manually.
   */
  const toggleMode = useCallback(() => {
    if (forceDemo) {
      setForceDemo(false);
      // Will re-check on next effect cycle
    } else {
      setForceDemo(true);
      setMode('DEMO');
      setLiveData(null);
    }
  }, [forceDemo]);

  // Initial detection
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate data fetch / one-time init on mount
    detectMode();
  }, [detectMode]);

  // Fetch data when mode becomes LIVE
  useEffect(() => {
    if (mode === 'LIVE') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- legitimate data fetch / one-time init on mount
      refreshData();
    }
  }, [mode, refreshData]);

  // Set up refresh interval
  useEffect(() => {
    if (mode === 'LIVE' && refreshIntervalMs > 0) {
      intervalRef.current = setInterval(refreshData, refreshIntervalMs);
      return () => clearInterval(intervalRef.current);
    }
  }, [mode, refreshIntervalMs, refreshData]);

  // Periodically re-check if proxy came online (when in demo mode)
  useEffect(() => {
    if (mode === 'DEMO' && !forceDemo) {
      const recheck = setInterval(detectMode, 30000); // check every 30s
      return () => clearInterval(recheck);
    }
  }, [mode, forceDemo, detectMode]);


  /**
   * Phase 41B: owner-only "return to truth" action.
   * Clears forced demo state, asks the proxy to clear/recheck live status,
   * then loads a fresh snapshot into the dashboard.
   */
  const resetAndRecheckLive = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setForceDemo(false);
    setMode('CHECKING');

    try {
      const result = await runAdminLiveRecheck();

      if (!result?.health || result.health.status !== 'ok') {
        setMode('DEMO');
        setError(result?.message || 'Fast health check did not respond.');
        return result;
      }

      const data = result.snapshot || await fetchAllData();

      if (data && data.mode === 'LIVE') {
        setMode('LIVE');
        setLiveData(data);
        setLastUpdated(new Date(data.fetchedAt || Date.now()));

        if (data.truthState === 'NO_LIVE_SIGNAL') {
          setError(data.statusMessage || 'Live proxy is running, but API data is incomplete. Showing a safe placeholder signal.');
        } else if (data.truthState === 'PARTIAL') {
          const parts = data.errors?.map((item) => item.source).join(', ');
          setError(data.statusMessage || (parts ? `Partial data — check: ${parts}` : 'Partial data — some APIs may be unavailable'));
        } else {
          setError(null);
        }
      } else {
        setMode('DEMO');
        setError(result?.message || 'Live recheck finished, but no usable dashboard snapshot was returned.');
      }

      return result;
    } catch (err) {
      setMode('DEMO');
      setError(err?.message || 'Reset/Recheck Live failed.');
      return {
        ok: false,
        message: err?.message || 'Reset/Recheck Live failed.',
        finishedAt: new Date().toISOString(),
      };
    } finally {
      setIsLoading(false);
    }
  }, []);


  return {
    mode,           // 'CHECKING' | 'LIVE' | 'DEMO'
    liveData,       // The full data object from /api/all (or null in demo)
    lastUpdated,    // Date of last successful fetch
    error,          // Error message if any
    isLoading,      // True during active fetch
    isLive: mode === 'LIVE',
    isDemo: mode === 'DEMO',
    refreshData,    // Manual refresh function
    resetAndRecheckLive, // Owner-only reset/recheck helper for Admin Health
    toggleMode,     // Switch between live/demo
    forceDemo,      // Whether demo is forced
  };
}
